import type { ElementType } from 'react';
import { cn } from './utils/cn';

interface StatisticCardProps {
  title: string;
  value: string | number | null;
  icon: ElementType;
  hint?: string;
  tone?: 'primary' | 'success' | 'warning' | 'neutral';
  loading?: boolean;
  appearance?: 'compact' | 'highlight';
}

const tones = {
  primary: 'bg-primary/10 text-primary',
  success: 'bg-emerald-50 text-emerald-700',
  warning: 'bg-amber-50 text-amber-700',
  neutral: 'bg-slate-100 text-slate-600',
};

const accents = { primary: 'bg-primary', success: 'bg-emerald-500', warning: 'bg-amber-500', neutral: 'bg-slate-400' };

export function StatisticCard({ title, value, icon: Icon, hint, tone = 'primary', loading = false, appearance = 'compact' }: StatisticCardProps) {
  if (appearance === 'highlight') {
    const backgrounds = { primary: 'bg-primary', success: 'bg-emerald-600', warning: 'bg-amber-600', neutral: 'bg-slate-800' };
    return <section aria-label={title} aria-busy={loading} className={cn('flex items-center gap-4 rounded-xl px-5 py-4 text-white shadow-sm', backgrounds[tone])}>
      <Icon aria-hidden className="h-6 w-6 shrink-0 opacity-80" />
      <dl className="min-w-0"><dt className="text-xs font-medium text-white/80">{title}</dt><dd className="mt-1 text-3xl font-semibold tracking-tight tabular-nums">{loading ? <span role="status" aria-label={`Carregando ${title}`} className="block h-8 w-16 animate-pulse rounded bg-white/20" /> : value ?? '—'}</dd></dl>
    </section>;
  }
  return <section aria-label={title} aria-busy={loading} className="relative overflow-hidden rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
    <span aria-hidden="true" className={cn('absolute inset-y-0 left-0 w-1', accents[tone])} />
    <div className="flex items-center gap-3">
      <span aria-hidden="true" className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-lg', tones[tone])}><Icon className="h-5 w-5" /></span>
      <dl className="flex min-w-0 flex-col">
        <dt className="order-2 mt-1 text-xs font-medium text-slate-500">{title}</dt>
        <dd className="order-1 text-2xl font-semibold leading-none tracking-tight text-slate-900 tabular-nums">
          {loading ? <span role="status" aria-label={`Carregando ${title}`} className="block h-6 w-16 animate-pulse rounded-md bg-slate-100" /> : value ?? '—'}
        </dd>
      </dl>
    </div>
    {hint && <p className="mt-2 text-xs text-slate-500">{hint}</p>}
  </section>;
}
